import { ServiceArea } from '@kalos/kalos-rpc';
import { useQuery } from '@tanstack/react-query';

import { ServiceAreaClientService } from '../../tools/helpers';
import { parseServiceAreaPolygon, type ServiceAreaPoint } from '../../tools/serviceArea';
import { queryKeys } from './constants';

export type ActiveServiceArea = {
  // Copy for the dispatch map legend, e.g. "All divisions".
  name: string;
  // Open ring: the last vertex joins the first and is not repeated. Empty when
  // there is no active boundary or the stored polygon will not parse, which is
  // the inert case -- the map draws nothing and nothing is flagged.
  polygon: ServiceAreaPoint[];
};

const EMPTY: ActiveServiceArea = { name: '', polygon: [] };

// The boundary changes when the company's coverage does, which is a SQL edit
// measured in months. The backend caches the row for five minutes anyway, so a
// shorter staleTime here would only buy refetches that return the same vertices.
const STALE_TIME = 5 * 60 * 1000;

export const getActiveServiceAreaQueryConfig = () => ({
  queryKey: [queryKeys.serviceArea.root, queryKeys.serviceArea.active],
  queryFn: async (): Promise<ActiveServiceArea> => {
    const req = ServiceArea.create({ isActive: true });
    const res = await ServiceAreaClientService.BatchGet(req);

    // Newest-wins, matching PolygonCache.Active on the Go side: nothing in the
    // schema stops a second row being marked active, and taking the highest id
    // makes inserting a new boundary without clearing the old one the intended
    // action rather than a silently ignored one.
    const active = res.results.reduce<ServiceArea | undefined>(
      (newest, row) => (!newest || row.id > newest.id ? row : newest),
      undefined,
    );
    if (!active) return EMPTY;

    return { name: active.name, polygon: parseServiceAreaPolygon(active.polygon) };
  },
  staleTime: STALE_TIME,
});

export const useActiveServiceAreaQuery = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery({ ...getActiveServiceAreaQueryConfig(), enabled });
